import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useRecentEventForCurrentMode } from "../../../../../hooks/useRecentEventForCurrentMode";
import {
  selectFocusedEvent,
  selectIsSoftball,
} from "../../../../../store/slices/socket/selectors";
import type { RootState } from "../../../../../store/store";
import { getFallbackBallPosition } from "../../../../../utils/getFallbackBallPosition";
import { nudgeBallPosition } from "../../../../../utils/nudgeBallPosition";
import { StrikeZoneVisualization } from "../../svg/StrikeZoneVisualization";

interface Props {
  perspective: "pitcher" | "catcher";
  twoStrikeZone: boolean;
  style?: React.CSSProperties;
  hideBall?: boolean;
}

export const StrikeZone = ({
  style,
  perspective,
  twoStrikeZone,
  hideBall,
}: Props) => {
  const { isViewingPreviousEvent } = useSelector(
    (state: RootState) => state.socket,
  );
  const focusedEvent = useSelector(selectFocusedEvent);
  const recentEvent = useRecentEventForCurrentMode();
  const isSoftball = useSelector(selectIsSoftball);
  const isInStatTrackingMode = useSelector(
    (state: RootState) => state.session.isInStatTrackingMode,
  );
  const activeView = useSelector((state: RootState) => state.view.activeView);

  const activeEvent = isViewingPreviousEvent ? focusedEvent : recentEvent;
  const pitch_data = activeEvent?.pitch_data;

  // Track which event UUID has completed its 2-second delay. Ball visibility is
  // derived synchronously each render to prevent a one-frame flash.
  const [revealedUuid, setRevealedUuid] = useState<string | undefined>(
    undefined,
  );

  const currentUuid = activeEvent?.event_uuid;
  const shouldApplyDelay =
    isInStatTrackingMode &&
    !isViewingPreviousEvent &&
    activeView !== "Challenge Mode";
  const ballVisible =
    !shouldApplyDelay || !currentUuid || revealedUuid === currentUuid;

  useEffect(() => {
    if (!shouldApplyDelay) {
      setRevealedUuid(undefined);
    }
  }, [shouldApplyDelay]);

  useEffect(() => {
    if (!shouldApplyDelay || !currentUuid) return;

    const timer = setTimeout(() => setRevealedUuid(currentUuid), 2000);
    return () => clearTimeout(timer);
  }, [currentUuid, shouldApplyDelay]);

  const shouldHideBall = hideBall || !ballVisible;

  const hasRelativeCoordinates =
    pitch_data &&
    typeof pitch_data.PlateLocSideSZRelative === "number" &&
    typeof pitch_data.PlateLocHeightSZRelative === "number";

  let ballX: number | undefined;
  let ballY: number | undefined;
  let isStrike: boolean | undefined;

  if (!shouldHideBall && pitch_data) {
    if (hasRelativeCoordinates) {
      const relativeSideX = pitch_data.PlateLocSideSZRelative as number;
      const relativeHeightY = pitch_data.PlateLocHeightSZRelative as number;
      isStrike = pitch_data.AutoPitchCall === "strike";

      // Apply position nudging for visual clarity on close calls
      const nudged = nudgeBallPosition({
        plateX: relativeSideX,
        plateY: relativeHeightY,
        isStrike: isStrike ?? false,
        isSoftball,
      });

      ballX = nudged.plateX * (perspective === "pitcher" ? 50 : -50) + 50;
      ballY = 100 - nudged.plateY * 100;
    } else {
      const fallback = getFallbackBallPosition({
        plateLocSideFeet: pitch_data.PlateLocSideFeet,
        plateLocHeightFeet: pitch_data.PlateLocHeightFeet,
        zoneAccuracy: pitch_data.ZoneAccuracy,
        perspective,
        isSoftball,
      });

      if (fallback) {
        ballX = fallback.ballX;
        ballY = fallback.ballY;
        isStrike = fallback.isStrike;
      }
    }
  }

  return (
    <StrikeZoneVisualization
      twoStrikeZone={twoStrikeZone}
      perspective={perspective}
      style={
        style ? style : { width: "35%", maxHeight: "42%", overflow: "visible" }
      }
      ballX={ballX}
      ballY={ballY}
      isStrike={isStrike}
    />
  );
};
